// REACT IMPORTS
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

// COMPONENT IMPORTS
import Button from "components/form/Button";
import MessageToast from "components/ui-component/MessageToast";
import { useServerError } from "hooks/useServerError";
import { LOGIN_ERROR_MESSAGES } from "utils/apiError";
import { InfoCircleIcon } from "icons/warning/InfoCircleIcon";
import {
  otpVerify,
  sendVerification,
  updateToken,
  updateUserData,
  useAccountDetails,
  useUserData,
} from "store/slices/authSlice";
import { useAppDispatch } from "store/hooks";
import { onSetIsLogin } from "store/slices/commonSlice";
import { clearSlice } from "utils/HelperFunctions";
import { TOKEN_STORAGE_KEY, TOKEN_STORAGE_TIME } from "store/api/axiosApi";
import { useTid } from "utils/testid";
import { activateOnKey } from "utils/activateOnKey";

/* ------------------------------------- OTP VERIFICATION FORM ------------------------------------- */

// Set when the form is shown inside the guest login overlay of a link page:
// the overlay says where the code went and is told when it is verified.
export interface GuestVerifyOtp {
  destination: any;
  onVerified: () => void;
  onCancel: () => void;
}

const OtpVerificationForm = ({ guest }: { guest?: GuestVerifyOtp }) => {
  const tid = useTid();
  const { state: navigationState } = useLocation();
  // Fall back to the stored destination so a refresh or direct link still
  // shows which phone/email the code was sent to
  const state = React.useMemo(() => {
    if (guest) {
      return guest.destination;
    }
    if (navigationState?.email || navigationState?.number) {
      return navigationState;
    }
    try {
      const stored = sessionStorage.getItem("otp-destination");
      return stored ? JSON.parse(stored) : navigationState;
    } catch {
      return navigationState;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationState, guest?.destination]);
  const COUNTDOWN = 60;
  const SUCCESS_FEEDBACK_MS = 750;

  const dispatch = useAppDispatch();
  const userData = useUserData();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [valid, setValid] = useState<boolean>(false);
  const [seconds, setSeconds] = useState<number>(COUNTDOWN);
  const [message, setMessage] = useState<string>(
    "The OTP you entered is invalid. Please try again."
  );
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [wrongOtp, setWrongOtp] = useState<boolean>(false);
  const [resentMessage, setResentMessage] = useState<string | null>(null);
  const { serverError, showError, clearError } = useServerError();

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const accountDetails = useAccountDetails();
  const { clusterId: cluster_id } = useParams();
  useEffect(() => {
    const timer = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);
      }
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds]);

  useEffect(
    () => () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    // Editing the code clears the last server error.
    clearError();
    if (otp.every((char) => char.trim() !== "")) {
      onSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = e.target.value;
    // SMS autofill drops the whole code into the first box
    if (/^\d{6}$/.test(value)) {
      setOtp(value.split(""));
      return;
    }
    if (!/^\d*$/.test(value) || value.length > 1) return; // Allow only numeric values

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (index < 6 - 1 && value !== "") {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && index > 0 && !otp[index]) {
      setWrongOtp(false);
      const newOtp = [...otp];
      newOtp[index - 1] = "";
      setOtp(newOtp);
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault(); // Prevent the default paste behavior

    const pastedText = e.clipboardData.getData("text").trim();

    if (/^\d{6,}$/.test(pastedText)) {
      // Longer runs of digits are cut down to the code's 6
      const newOtp = pastedText.slice(0, 6).split("");
      setOtp(newOtp);

      // Focus on the first input field
      inputRefs.current[0]?.focus();
    }
  };

  const onSubmit = async () => {
    setIsLoading(true);
    clearError();
    try {
      let payload = {
        otp: otp?.join(""),
        accounts_id: userData?.data?.accountData?.id,
      };
      let res: any = await dispatch(otpVerify(payload)).unwrap();
      const verifiedToken = res?.body?.token;
      if (res?.body?.success && verifiedToken) {
        localStorage.setItem(TOKEN_STORAGE_KEY, verifiedToken);
        localStorage.setItem(TOKEN_STORAGE_TIME, `${new Date().getTime()}`);
        // Login only returns the account id while the OTP is outstanding; the
        // profile comes back with the verified token.
        const verifiedAccount = res?.body?.data?.accountData;
        if (verifiedAccount) {
          dispatch(updateUserData(verifiedAccount));
        }
        dispatch(updateToken({ token: verifiedToken }));
        setValid(true);
        sessionStorage.removeItem("otp-destination");

        redirectTimerRef.current = setTimeout(() => {
          dispatch(onSetIsLogin(true));
          if (guest) {
            // Stay on the link page; the overlay closes.
            guest.onVerified();
            return;
          }
          if (
            accountDetails?.data?.landing_page &&
            accountDetails?.data?.landing_page?.toLowerCase() === "payment"
          ) {
            navigate(`/${cluster_id}/make-payment`);
          } else if (
            accountDetails?.data?.landing_page &&
            accountDetails?.data?.landing_page?.toLowerCase() === "profile"
          ) {
            navigate(`/${cluster_id}/my-profile`);
          } else {
            navigate(`/${cluster_id}/dashboard`);
          }
        }, SUCCESS_FEEDBACK_MS);
      } else if (typeof res?.body?.data?.msg === "string") {
        // A wrong, expired or used-up code: the server says which, under the
        // boxes.
        setMessage(
          res.body.data.msg ||
            "The OTP you entered is invalid. Please try again."
        );
        setWrongOtp(true);
      } else {
        // No reason about the code means the check itself failed.
        showError(res, LOGIN_ERROR_MESSAGES.verifyOtp);
      }
    } catch (error: any) {
      console.log("otp verify err: -->", error?.message);
      showError(error, LOGIN_ERROR_MESSAGES.verifyOtp);
    } finally {
      setIsLoading(false);
    }
  };

  const onResendOtp = async () => {
    setWrongOtp(false);
    setOtp(["", "", "", "", "", ""]);
    setSeconds(COUNTDOWN);
    inputRefs.current[0]?.focus();
    setResentMessage(null);
    clearError();
    try {
      let payload: any = {
        isEmail: state?.email?.value ? true : false,
        email_id: state?.email?.value || "",
        isPhone: state?.number?.value ? true : false,
        phone_number_id: state?.number?.value || "",
        accounts_id: userData?.data?.accountData?.id,
      };
      if (!payload?.isEmail) {
        delete payload["email_id"];
        delete payload["isEmail"];
      }
      if (!payload?.isPhone) {
        delete payload["phone_number_id"];
        delete payload["isPhone"];
      }
      const res: any = await dispatch(sendVerification(payload)).unwrap();
      if (res?.body?.success) {
        const destination = state?.email?.name || state?.number?.name;
        setResentMessage(
          destination
            ? `A new code has been sent to ${destination}.`
            : "A new code has been sent."
        );
      } else {
        showError(res, LOGIN_ERROR_MESSAGES.resendOtp);
      }
    } catch (error: any) {
      console.log("send verification code error: -->", error?.message);
      showError(error, LOGIN_ERROR_MESSAGES.resendOtp);
    }
  };
  const handleLogOut = () => {
    if (guest) {
      guest.onCancel();
      return;
    }
    clearSlice(dispatch);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.clear();
    navigate(`/${cluster_id}/login`, { replace: true });
  };

  const canResend = wrongOtp || seconds === 0;

  return (
    <div
      className="animate__animated animate__fadeIn w-full lg:max-w-[520px]"
      data-testid={tid("form-shell")}
    >
      <MessageToast
        variant="success"
        content={resentMessage}
        width="w-full max-w-[442px] px-5"
        className="!right-1/2 !translate-x-1/2"
        onCloseClick={() => setResentMessage(null)}
      />
      <div className="lg:text-3xl text-xl font-bold -tracking-[0.6px] leading-normal text-txtPrimary">
        OTP Verification
      </div>
      <div className="lg:pt-[74px] pt-6">
        <p className="text-sm lg:text-base leading-normal font-normal text-txtParagraph">
          Enter the code we sent to
          <span className="font-semibold">
            &nbsp;
            {state?.email?.name ? state?.email?.name : state?.number?.name}
          </span>
        </p>
        <div
          className="flex justify-between lg:max-w-[520px] w-full pt-10"
          data-testid={tid("code-input-group")}
          aria-label="One-time passcode"
        >
          {otp.map((value, index) => (
            <input
              key={`otp-digit-${index + 1}`}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              value={value}
              onPaste={(e) => handlePaste(e)}
              onChange={(e) => handleChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              autoFocus={index === 0}
              data-testid={tid(`code-digit-${index + 1}-input`)}
              aria-label={`Verification code digit ${index + 1}`}
              className="w-10 h-10 lg:w-[60px] lg:h-[60px] sm:w-[50px] sm:h-[50px] text-center focus:outline-none bg-bgSecondary rounded-[6px] lg:text-3xl text-xl leading-normal font-bold text-txtPrimary"
            />
          ))}
        </div>
        {/* A wrong code and a failed server call share the box under the
            code boxes. A server error is always the latest result (it is
            cleared on every edit and attempt), so it wins over a wrong-code
            message left from an earlier try. */}
        {wrongOtp || serverError ? (
          <div className="pt-4">
            <div
              className="px-2.5 py-3 bg-lightRed rounded flex items-start gap-[5px] lg:max-w-[520px] w-full"
              role="alert"
              data-testid={tid(
                serverError ? "server-error-alert" : "invalid-code-alert"
              )}
            >
              <InfoCircleIcon
                strokeColor="var(--color-red)"
                aria-hidden="true"
              />
              <p className="text-red text-xs leading-normal pt-0.5">
                {serverError || message}
              </p>
            </div>
          </div>
        ) : null}
      </div>
      <div className="pt-10 flex flex-col items-center w-full lg:max-w-[520px] gap-2">
        <div className="flex flex-col items-center lg:gap-5 gap-4">
          {!wrongOtp ? (
            <div
              className="text-btnPrimaryActive lg:text-lg text-base font-normal leading-normal"
              data-testid={tid("resend-countdown")}
              aria-label={`Resend available in ${seconds} seconds`}
            >
              {`${Math.floor(seconds / 60)
                .toString()
                .padStart(2, "0")}:${(seconds % 60)
                .toString()
                .padStart(2, "0")}`}
            </div>
          ) : null}
          <div className="text-txtSecondary font-medium text-base leading-normal opacity-40">
            Didn’t receive the code?
          </div>
        </div>
        <div
          className={`lg:text-base text-sm font-medium leading-normal ${
            canResend
              ? "text-btnPrimaryActive cursor-pointer"
              : "text-strokeActive pointer-events-none"
          }`}
          role="button"
          tabIndex={canResend ? 0 : -1}
          aria-disabled={!canResend}
          aria-label="Resend verification code"
          data-testid={tid("resend-code-button")}
          onClick={() => canResend && onResendOtp()}
          onKeyDown={activateOnKey(() => canResend && onResendOtp())}
        >
          Resend code
        </div>
      </div>
      <div className="lg:max-w-[520px] lg:pt-[66px] pt-10">
        <Button
          isLoading={isLoading}
          className="w-full"
          text="Login"
          data-testid={tid("login-button")}
          aria-label="Continue to portal"
          isDisabled={!valid}
          isInteractionDisabled={valid}
          variant="variable"
          isRightLoader={true}
          icon={valid ? "TickCircleIcon" : null}
          onClick={() => {
            dispatch(onSetIsLogin(true));
            if (guest) {
              guest.onVerified();
              return;
            }
            if (
              accountDetails?.data?.landing_page &&
              accountDetails?.data?.landing_page?.toLowerCase() === "payment"
            ) {
              navigate(`/${cluster_id}/make-payment`);
            } else if (
              accountDetails?.data?.landing_page &&
              accountDetails?.data?.landing_page?.toLowerCase() === "profile"
            ) {
              navigate(`/${cluster_id}/my-profile`);
            } else {
              navigate(`/${cluster_id}/dashboard`);
            }
          }}
        />
        <p className="text-center lg:text-base text-sm text-blue hover:!text-btnPrimaryHover mt-4 w-full">
          <span
            className="cursor-pointer"
            onClick={handleLogOut}
            onKeyDown={activateOnKey(handleLogOut)}
            role="button"
            tabIndex={0}
            data-testid={tid("go-back-button")}
            aria-label="Go back to login"
          >
            Go back
          </span>{" "}
        </p>
      </div>
    </div>
  );
};

export default OtpVerificationForm;
